import React, { useEffect, useRef, useState } from 'react';
import { TypesOfConsiderationRow } from '../../../../slices/marketplace/loi-comparison/models/types-of-consideration-row.model';
import { TypeOfConsideration } from '../../../../slices/marketplace/pricing/models/type-of-consideration.enum';
import { ConsiderationTreatment } from '../../../../slices/marketplace/pricing/models/consideration-treatment.enum';
import { TypesOfConsiderationTable } from './TypesOfConsiderationTable';

const SAVE_DEBOUNCE_MS = 800;

export interface ConsiderationSectionItem {
    buyerId: number;
    buyerName: string;
    considerationRows: TypesOfConsiderationRow[];
    fairValueByType?: Partial<Record<TypeOfConsideration, number | null>>;
    faceValueByType?: Partial<Record<TypeOfConsideration, number | null>>;
    isLoiSelected?: boolean;
}

const getMirrorSource = (
    considerationType: TypeOfConsideration,
    feePaymentTiming: ConsiderationTreatment | null,
    item: ConsiderationSectionItem
): number | null | undefined => {
    if (feePaymentTiming === ConsiderationTreatment.FaceValueAtClosing) {
        return item.faceValueByType?.[considerationType];
    }
    if (feePaymentTiming === ConsiderationTreatment.FairValueAtClosing) {
        return item.fairValueByType?.[considerationType];
    }
    return undefined;
};

export interface TypesOfConsiderationSectionProps {
    items: ConsiderationSectionItem[];
    canEditTiming: boolean;
    canEditCashFlow: boolean;
    canEditComment: boolean;
    fullWidth?: boolean;
    levelOfService: number;
    onSave: (
        buyerId: number,
        allRows: TypesOfConsiderationRow[],
        changedRows: TypesOfConsiderationRow[]
    ) => void | Promise<void>;
}

export const TypesOfConsiderationSection = ({
    items,
    canEditTiming,
    canEditCashFlow,
    canEditComment,
    fullWidth = false,
    levelOfService,
    onSave,
}: TypesOfConsiderationSectionProps) => {
    const [rowsByBuyer, setRowsByBuyer] = useState<Record<number, TypesOfConsiderationRow[]>>({});
    const syncedRowsRef = useRef<Record<number, TypesOfConsiderationRow[]>>({});
    const dirtyTypesRef = useRef<Record<number, Set<TypeOfConsideration>>>({});
    const rowsByBuyerRef = useRef<Record<number, TypesOfConsiderationRow[]>>({});
    const timersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
    const chainRef = useRef<Record<number, Promise<void>>>({});
    const onSaveRef = useRef(onSave);

    useEffect(() => {
        onSaveRef.current = onSave;
    }, [onSave]);

    useEffect(() => {
        rowsByBuyerRef.current = rowsByBuyer;
    }, [rowsByBuyer]);

    useEffect(() => {
        const canEdit = canEditTiming || canEditCashFlow || canEditComment;
        const healedTypesByBuyer: Record<number, Set<TypeOfConsideration>> = {};

        setRowsByBuyer((prev) => {
            let changed = false;
            const next = { ...prev };
            items.forEach((item) => {
                if (syncedRowsRef.current[item.buyerId] !== item.considerationRows) {
                    syncedRowsRef.current[item.buyerId] = item.considerationRows;

                    let rows = item.considerationRows;
                    if (canEdit) {
                        const healedTypes = new Set<TypeOfConsideration>();
                        rows = rows.map((row) => {
                            const mirrorSource = getMirrorSource(
                                row.considerationType,
                                row.feePaymentTiming,
                                item
                            );
                            const isHealCandidate =
                                mirrorSource != null && row.projectedCashFlow !== mirrorSource;
                            if (!isHealCandidate) {
                                return row;
                            }
                            healedTypes.add(row.considerationType);
                            return { ...row, projectedCashFlow: mirrorSource };
                        });
                        if (healedTypes.size > 0) {
                            healedTypesByBuyer[item.buyerId] = healedTypes;
                        }
                    }

                    next[item.buyerId] = rows;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });

        Object.entries(healedTypesByBuyer).forEach(([buyerIdKey, healedTypes]) => {
            const buyerId = Number(buyerIdKey);
            healedTypes.forEach((considerationType) => markTypeDirty(buyerId, considerationType));
            scheduleFlush(buyerId);
        });
    }, [items, canEditTiming, canEditCashFlow, canEditComment]);

    const flushBuyer = (buyerId: number) => {
        const previousChain = chainRef.current[buyerId] ?? Promise.resolve();
        const nextChain = previousChain
            .then(() => {
                const dirtyTypes = dirtyTypesRef.current[buyerId];
                if (!dirtyTypes || dirtyTypes.size === 0) {
                    return undefined;
                }
                const rows = rowsByBuyerRef.current[buyerId] ?? [];
                const changedRows = rows.filter((row) => dirtyTypes.has(row.considerationType));
                dirtyTypesRef.current[buyerId] = new Set<TypeOfConsideration>();
                return onSaveRef.current(buyerId, rows, changedRows);
            })
            .catch(() => undefined);
        chainRef.current[buyerId] = nextChain;
    };

    const scheduleFlush = (buyerId: number) => {
        const existingTimer = timersRef.current[buyerId];
        if (existingTimer) {
            clearTimeout(existingTimer);
        }
        timersRef.current[buyerId] = setTimeout(() => {
            delete timersRef.current[buyerId];
            flushBuyer(buyerId);
        }, SAVE_DEBOUNCE_MS);
    };

    useEffect(() => {
        return () => {
            Object.values(timersRef.current).forEach((timer) => clearTimeout(timer));
            timersRef.current = {};
            Object.keys(dirtyTypesRef.current).forEach((key) => {
                const buyerId = Number(key);
                const dirtyTypes = dirtyTypesRef.current[buyerId];
                if (dirtyTypes && dirtyTypes.size > 0) {
                    flushBuyer(buyerId);
                }
            });
        };
    }, []);

    const getRowsForBuyer = (buyerId: number): TypesOfConsiderationRow[] =>
        rowsByBuyer[buyerId] ??
        items.find((item) => item.buyerId === buyerId)?.considerationRows ??
        [];

    const updateLocalConsiderationRow = (
        buyerId: number,
        considerationType: TypeOfConsideration,
        patch: Partial<TypesOfConsiderationRow>
    ) =>
        setRowsByBuyer((prev) => ({
            ...prev,
            [buyerId]: (
                prev[buyerId] ??
                items.find((item) => item.buyerId === buyerId)?.considerationRows ??
                []
            ).map((row) =>
                row.considerationType === considerationType ? { ...row, ...patch } : row
            ),
        }));

    const markTypeDirty = (buyerId: number, considerationType: TypeOfConsideration) => {
        const dirtyTypes = dirtyTypesRef.current[buyerId] ?? new Set<TypeOfConsideration>();
        dirtyTypes.add(considerationType);
        dirtyTypesRef.current[buyerId] = dirtyTypes;
    };

    const handleTimingChange = (
        buyerId: number,
        considerationType: TypeOfConsideration,
        patch: Partial<TypesOfConsiderationRow>
    ) => {
        markTypeDirty(buyerId, considerationType);
        updateLocalConsiderationRow(buyerId, considerationType, patch);
        scheduleFlush(buyerId);
    };

    const handleCashFlowChange = (
        buyerId: number,
        considerationType: TypeOfConsideration,
        projectedCashFlow: number | null
    ) => {
        markTypeDirty(buyerId, considerationType);
        updateLocalConsiderationRow(buyerId, considerationType, { projectedCashFlow });
        scheduleFlush(buyerId);
    };

    const handleCommentChange = (
        buyerId: number,
        considerationType: TypeOfConsideration,
        comment: string | null
    ) => {
        markTypeDirty(buyerId, considerationType);
        updateLocalConsiderationRow(buyerId, considerationType, { comment });
        scheduleFlush(buyerId);
    };

    const handleBlurSave = (buyerId: number) => {
        const dirtyTypes = dirtyTypesRef.current[buyerId];
        if (!dirtyTypes || dirtyTypes.size === 0) {
            return;
        }
        scheduleFlush(buyerId);
    };

    return (
        <TypesOfConsiderationTable
            items={items.map(
                ({ buyerId, buyerName, fairValueByType, faceValueByType, isLoiSelected }) => ({
                    buyerId,
                    buyerName,
                    considerationRows: getRowsForBuyer(buyerId),
                    fairValueByType,
                    faceValueByType,
                    isLoiSelected,
                })
            )}
            canEditTiming={canEditTiming}
            canEditCashFlow={canEditCashFlow}
            canEditComment={canEditComment}
            fullWidth={fullWidth}
            levelOfService={levelOfService}
            onTimingChange={handleTimingChange}
            onCashFlowChange={handleCashFlowChange}
            onCommentChange={handleCommentChange}
            onBlurSave={handleBlurSave}
        />
    );
};
